import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useCart } from "../context/CartContext";

// Polls the backend for payment status. Stripe webhooks can be delayed in
// preview environments, so /payments/status also verifies via Stripe API.
const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const r = await api.paymentStatus(sessionId);
        if (r.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (["failed", "expired"].includes(r.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      attempts.current += 1;
      if (attempts.current >= 15) { setStatus("pending"); return; }
      setTimeout(poll, 2000);
    };

    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="px-6 md:px-10 max-w-[720px] mx-auto py-24 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <p className="font-serif italic text-[color:var(--ink-mute)]">Payment</p>
          <h1 className="font-serif text-5xl mt-4">Confirming your order…</h1>
          <p className="font-serif text-[color:var(--ink-mute)] mt-4">Just a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <p className="font-serif italic text-[color:var(--ink-mute)]">Payment complete</p>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.1]">Thank you.</h1>
          <p className="font-serif text-xl text-[color:var(--ink-mute)] mt-6">
            Your order was received. A receipt has been sent to your email.
          </p>
          <div className="mt-10 flex items-center justify-center gap-6">
            <Link to="/shop" className="font-serif italic tlink">Back to shop →</Link>
            <Link to="/" className="font-serif italic tlink tlink-mute">Home</Link>
          </div>
        </>
      )}
      {status === "pending" && (
        <>
          <p className="font-serif italic text-[color:var(--ink-mute)]">Payment</p>
          <h1 className="font-serif text-4xl mt-4">Still processing.</h1>
          <p className="font-serif text-[color:var(--ink-mute)] mt-6">
            Your payment is taking longer than usual to confirm. You will receive an email
            once it's processed. You can safely close this page.
          </p>
          <div className="mt-10">
            <Link to="/" className="font-serif italic tlink">Return home →</Link>
          </div>
        </>
      )}
      {status === "error" && (
        <>
          <p className="font-serif italic text-[color:var(--ink-mute)]">Payment</p>
          <h1 className="font-serif text-4xl mt-4">Something went wrong.</h1>
          <p className="font-serif text-[color:var(--ink-mute)] mt-6">
            We could not verify your payment. If you were charged, please contact us and we will
            resolve it immediately.
          </p>
          <div className="mt-10 flex items-center justify-center gap-6">
            <Link to="/contact" className="font-serif italic tlink">Contact →</Link>
            <Link to="/cart" className="font-serif italic tlink tlink-mute">Back to cart</Link>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
